import { Node } from '@tiptap/core';
import { blockNodeSpec, dataAttribute } from './block';
import { blockViews, initialView } from './block_views';

// Episode reference: a card pointing at another episode of the podcast, with an optional note. Saved as a
// bare block placeholder with data-episode-reference-id (an opaque signed token, never the episode's own id)
// and data-episode-reference-note; Newsletters::RenderBlocks renders it and re-resolves the episode within the
// post's podcast at render time. The editor chrome lives in dashboard/shared/tiptap/_episode_reference_editor.html.erb
// and the preview comes from the same server component. (data-episode-reference, without a suffix, is the
// unrelated flag on mention links in tiptap_episode_mentions.js.)
//
// The page passes data-tiptap-block-capabilities-value.episode_reference = { post_id, search_path,
// suggestions_path }. The block shows one view at a time (block_views.js): "Mentioned in your draft" (POSTs the
// draft's text to suggestions_path, shown only when archive episodes match), the editor (the chosen episode +
// note, and a search over the podcast's episodes that lists the most recent ones until you type), or the
// preview. A reference with an episode opens in its preview; a new one opens in the editor and moves to the
// suggestions when they arrive and nothing has been typed yet.

const PREVIEW_DELAY_MS = 300;
const SEARCH_DELAY_MS = 250;

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

const fetchJson = async (url, options = {}) => {
  try {
    const res = await fetch(url, {
      credentials: 'include',
      ...options,
      headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken(), ...(options.headers || {}) },
    });
    return res.ok ? await res.json() : null;
  } catch (_) {
    return null;
  }
};

export const EpisodeReference = Node.create({
  name: 'episodeReference',

  ...blockNodeSpec('episode_reference', {
    attributes: {
      episodeReferenceId: dataAttribute('episodeReferenceId'),
      episodeReferenceNote: dataAttribute('episodeReferenceNote'),
    },
  }),

  addOptions() {
    return {
      templates: null,         // { editor_html } — injected by the tiptap controller from the server template
      capabilities: null,      // { post_id, search_path, suggestions_path } — injected by the tiptap controller
      fetchRenderedHtml: null, // async (attrs) => string | null — injected by the tiptap controller
      onRenderedHtmlFetched: null,
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const extension = this;
      const capabilities = extension.options.capabilities || {};
      let currentNode = node;
      let previewTimer = null;
      let previewRequest = 0;
      let searchTimer = null;
      let searchRequest = 0;
      let suggestions = [];
      let suggestionsLoaded = false;
      let changing = false; // the search is open on top of an already chosen episode
      let selectedTitle = '';
      let touched = false;   // the person has started searching, so late suggestions must not take the view away

      const dom = document.createElement('div');
      dom.setAttribute('data-episode-reference-node', '');
      dom.innerHTML = extension.options.templates?.editor_html
        || '<div class="my-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500">Episode reference</div>';

      const $ = selector => dom.querySelector(selector);
      const selected = $('[data-selected]');
      const selectedLabel = $('[data-selected-title]');
      const search = $('[data-search]');
      const preview = $('[data-preview]');
      const previewMissing = $('[data-preview-missing]');
      const noteField = $('[data-field="note"]');
      const searchInput = $('[data-search-input]');
      const results = $('[data-results]');
      const resultsLabel = $('[data-results-label]');
      const noResults = $('[data-no-results]');

      const hasSelection = () => Boolean(currentNode.attrs.episodeReferenceId);

      const setAttrs = attrs => {
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...currentNode.attrs, ...attrs }));
      };

      // The editor shows the chosen episode and its note, and the search only until one is chosen (or Change).
      const render = () => {
        selected?.classList.toggle('hidden', !hasSelection());
        search?.classList.toggle('hidden', hasSelection() && !changing);
        if (selectedLabel) selectedLabel.textContent = selectedTitle || 'Episode chosen';
        suggestionButtons().forEach(button => { button.textContent = hasSelection() ? 'Swap in' : 'Use'; });
        views?.refresh();
      };

      const suggestionButtons = () => Array.from(dom.querySelectorAll('[data-suggestions] [data-use]'));

      const rowArtwork = (row, card) => {
        const img = row.querySelector('[data-artwork]');
        if (img && card.artwork_url) {
          img.src = card.artwork_url;
          img.classList.remove('hidden');
        }
      };

      const choose = card => {
        changing = false;
        selectedTitle = [card.number_label, card.title].filter(Boolean).join(' · ');
        setAttrs({ episodeReferenceId: card.id });
        if (searchInput) searchInput.value = '';
        views.showEditor();
        render();
      };

      const renderSuggestions = () => {
        const list = $('[data-suggestions]');
        const template = $('template[data-suggestion-row]');
        if (!list || !template) return;
        list.replaceChildren();
        suggestions.forEach(card => {
          const row = template.content.firstElementChild.cloneNode(true);
          row.querySelector('[data-title]').textContent = [card.number_label, card.title].filter(Boolean).join(' · ');
          rowArtwork(row, card);
          row.querySelector('[data-use]').addEventListener('click', () => choose(card));
          list.appendChild(row);
        });
      };

      // The draft's text goes to the server once; archive episodes it mentions come back.
      const loadSuggestions = async () => {
        if (suggestionsLoaded || !capabilities.suggestions_path) return;
        suggestionsLoaded = true;
        const body = JSON.stringify({ text: editor.getText(), post_id: capabilities.post_id || '' });
        const data = await fetchJson(capabilities.suggestions_path, {
          method: 'POST',
          body,
          headers: { 'Content-Type': 'application/json' },
        });
        suggestions = data?.episodes || [];
        renderSuggestions();
        render();
        if (suggestions.length > 0 && views.view === 'editor' && !hasSelection() && !touched) views.showSuggestions();
      };

      const rowMeta = card => [card.length_label, card.date_label].filter(Boolean).join(' · ');

      const renderResults = (cards, query) => {
        const template = $('template[data-result-row]');
        if (!results || !template) return;
        results.replaceChildren();
        cards.forEach(card => {
          const row = template.content.firstElementChild.cloneNode(true);
          const number = row.querySelector('[data-number]');
          number.textContent = card.number_label || '';
          row.querySelector('[data-number-sep]').classList.toggle('hidden', !card.number_label);
          row.querySelector('[data-title]').textContent = card.title;
          row.querySelector('[data-meta]').textContent = rowMeta(card);
          rowArtwork(row, card);
          row.addEventListener('click', () => choose(card));
          results.appendChild(row);
        });
        if (resultsLabel) resultsLabel.textContent = query ? 'Results' : 'Recent';
        resultsLabel?.classList.toggle('hidden', cards.length === 0);
        noResults?.classList.toggle('hidden', cards.length > 0);
      };

      const runSearch = async () => {
        if (!capabilities.search_path) return;
        const query = searchInput?.value.trim() || '';
        const request = ++searchRequest;
        const params = new URLSearchParams({ q: query });
        const data = await fetchJson(`${capabilities.search_path}?${params}`);
        if (request === searchRequest && data) renderResults(data.episodes || [], query);
      };

      const showPreview = html => {
        if (!preview) return;
        preview.innerHTML = html || '';
        previewMissing?.classList.toggle('hidden', Boolean(html) || !hasSelection());
      };

      const schedulePreview = () => {
        if (!extension.options.fetchRenderedHtml || views?.view !== 'preview') return;
        clearTimeout(previewTimer);
        if (!hasSelection()) {
          showPreview('');
          return;
        }
        previewTimer = setTimeout(async () => {
          const request = ++previewRequest;
          const html = await extension.options.fetchRenderedHtml({ ...currentNode.attrs });
          if (request === previewRequest) showPreview(html);
        }, PREVIEW_DELAY_MS);
      };

      const syncFromNode = () => {
        if (noteField && noteField !== document.activeElement) noteField.value = currentNode.attrs.episodeReferenceNote ?? '';
        render();
      };

      noteField?.addEventListener('input', e => setAttrs({ episodeReferenceNote: e.target.value }));

      searchInput?.addEventListener('input', () => {
        touched = true;
        clearTimeout(searchTimer);
        searchTimer = setTimeout(runSearch, SEARCH_DELAY_MS);
      });

      $('[data-search-episodes]')?.addEventListener('click', () => {
        changing = hasSelection();
        touched = true;
        render();
        views.showEditor({ focus: true });
        runSearch();
      });

      $('[data-change]')?.addEventListener('click', () => {
        changing = true;
        render();
        runSearch();
        loadSuggestions();
        searchInput?.focus();
      });

      $('[data-remove]')?.addEventListener('mousedown', e => {
        e.preventDefault();
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        const current = editor.state.doc.nodeAt(pos);
        if (current) editor.chain().focus().deleteRange({ from: pos, to: pos + current.nodeSize }).run();
      });

      // The preview is made of real links; never navigate away from the editor.
      preview?.addEventListener('click', e => e.preventDefault());

      const views = blockViews(dom, {
        canFinish: hasSelection,
        hasSuggestions: () => suggestions.length > 0,
        onChange: view => {
          if (view !== 'editor' && changing) {
            changing = false;
            render();
          }
          if (view === 'preview') schedulePreview();
        },
      });
      syncFromNode();
      views.setView(initialView({ hasContent: hasSelection() }));
      if (!hasSelection()) {
        runSearch();
        loadSuggestions();
      }

      return {
        dom,
        // Typing in the fields must not reach ProseMirror (Backspace/Enter would act on the node).
        stopEvent: event => Boolean(event.target?.closest?.('input, textarea, button, a')),
        ignoreMutation: () => true,
        update(updatedNode) {
          if (updatedNode.type.name !== 'episodeReference') return false;
          currentNode = updatedNode;
          syncFromNode();
          schedulePreview();
          return true;
        },
        destroy() {
          clearTimeout(previewTimer);
          clearTimeout(searchTimer);
        },
      };
    };
  },

  addCommands() {
    return {
      insertEpisodeReference: (attrs = {}) => ({ chain }) =>
        chain().insertContent({ type: this.name, attrs }).run(),
    };
  },
});

// Offered on any post: a reference is useful whether or not the post has an episode of its own.
export const episodeReferenceSlashItem = {
  title: 'Episode reference',
  description: 'A card that points readers to another episode',
  icon: '🎧',
  group: 'Blocks',
  keywords: ['episode', 'reference', 'archive', 'link', 'listen', 'mention'],
  command: ({ editor, range }) => {
    editor.chain().focus().deleteRange(range).insertEpisodeReference().run();
  },
};
